import Column from "@amzn/meridian/column";
import Heading from "@amzn/meridian/heading";
import Row from "@amzn/meridian/row";
import SideMenu, {
  SideMenuLink,
  SideMenuTitle,
} from "@amzn/meridian/side-menu";
import Text from "@amzn/meridian/text";
import Thumbnail from "@amzn/meridian/thumbnail";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  setAllFavoriteMessages,
  setConversationId,
  setInputText,
  setIsInputDisabled,
  setIsLoading,
  setResponses,
  setShowResult,
  clearStreamingHistory,
} from "src/components/pages/home/store/chatSlice";
import { BADGE_PHOTO_THUMBNAIL_URL } from "src/config/external-links";
import {
  ALIAS,
  CONVERSATION_ID,
  FAVORITE_QUESTION_CLICK,
  HISTORY_QUESTION_CLICK,
  NEW_CHAT_CLICK,
  TOAST_TIMEOUT,
} from "src/constants/constants";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { sendCustomRUMEvent } from "src/utils/awsRUM";
import { v4 as uuidv4 } from "uuid";
import "./sideMenu.scss";
import { Button, Icon } from "@amzn/meridian";
import starFilledTokens from "@amzn/meridian-tokens/base/icon/star-filled";
import { createToast } from "src/store/toastsSlice";
import deleteTokens from "@amzn/meridian-tokens/base/icon/delete";
import ChatService from "src/components/pages/home/service/chatService";

const ChatSideMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const homeRoute =
    location.pathname === "/saige"
      ? "/saige"
      : location.pathname === "/mcp"
        ? "/mcp"
        : "/";
  const dispatch = useAppDispatch();
  const {
    recentChats,
    isRecentChatsLoaded,
    isFavoriteMessagesLoaded,
    favoriteMessages,
  } = useAppSelector((state) => state.chatSlice);
  const { alias, firstName, isAuthorized, isAdministrator } = useAppSelector(
    (state) => state.user,
  );

  const [openLinkHash, setOpenLinkHash] = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);
  const [hash, setHash] = useState("#newChat");
  const [open, setOpen] = useState(false);
  const onClose = () => setOpen(false);
  const onOpen = () => setOpen(true);

  // Update hash when admin status is confirmed
  useEffect(() => {
    if (isAdministrator) {
      setHash("#adminReport");
    }
  }, [isAdministrator]);

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  const handleDeleteConfirmation = async (messageId: string) => {
    try {
      const { data } = await ChatService.removeFavorite(messageId);

      if (data.successful) {
        const favoriteMessages = await ChatService.getAllFavorites();
        dispatch(setAllFavoriteMessages(favoriteMessages));
      }
    } catch (error) {
      {
        dispatch(
          createToast({
            type: "error",
            message: "Failed to delete favorite",
            timeout: TOAST_TIMEOUT,
          }),
        );
      }
    }
  };

  const handleAdminReport = (hash: string) => {
    setHash(hash);
    navigate("/admin"); // Navigate to admin report route
    onClose();
  };

  const initializeChatContext = (user_text) => {
    navigate(homeRoute); // Navigate to current home context
    dispatch(setShowResult(false));
    dispatch(setResponses(null));
    dispatch(clearStreamingHistory());
    dispatch(setIsInputDisabled(false));
    dispatch(setIsLoading(false));
    dispatch(setInputText(user_text));
    dispatch(setConversationId(uuidv4()));
  };

  const newChat = () => {
    initializeChatContext("");
    sendCustomRUMEvent(NEW_CHAT_CLICK, {
      [ALIAS]: alias,
    });
    onClose();
  };

  const clickHistoryQuestion = (user_text) => {
    initializeChatContext(user_text); // Always navigate to home for recent chats
    sendCustomRUMEvent(HISTORY_QUESTION_CLICK, {
      [ALIAS]: alias,
      [CONVERSATION_ID]: setConversationId,
    });
  };

  const clickFavoriteQuestion = (user_text) => {
    initializeChatContext(user_text);
    sendCustomRUMEvent(FAVORITE_QUESTION_CLICK, {
      [ALIAS]: alias,
      [CONVERSATION_ID]: setConversationId,
    });
  };

  return (
    <Row alignmentVertical="stretch" spacing="none">
      <SideMenu open={open} onClose={onClose} onOpen={onOpen} type="skinny">
        <SideMenuTitle>
          <Column>
            <Row alignmentHorizontal="center">
              <Thumbnail
                source={`${BADGE_PHOTO_THUMBNAIL_URL}${alias}`}
              ></Thumbnail>
            </Row>
            <Heading level={5} alignment="center">
              Hello, {firstName}
            </Heading>
          </Column>
        </SideMenuTitle>
        {isAdministrator && (
          <SideMenuLink
            href="#adminReport"
            selected={hash === "#adminReport"}
            onClick={(hash) => handleAdminReport(hash)}
          >
            Admin Report
          </SideMenuLink>
        )}
        <SideMenuLink
          href="#newChat"
          selected={hash === "#newChat"}
          onClick={(hash) => {
            setHash(hash);
            newChat();
          }}
        >
          New Chat
        </SideMenuLink>
        <SideMenuLink
          href="#favoritequestions"
          selected={hash === "#favoritequestions"}
          onClick={(hash) => {
            navigate(homeRoute);
            setHash(hash);
            setOpenLinkHash(
              openLinkHash === "#favoritequestions" ? null : hash,
            );
            setExpandedSession(
              expandedSession === "#favoritequestions"
                ? null
                : "#favoritequestions",
            );
          }}
          disabled={favoriteMessages?.length === 0}
        >
          <span className={"side-menu-favorite-title"}>
            Favorite Questions
            <Icon className="side-menu-star-icon" tokens={starFilledTokens} />
          </span>

          {isAuthorized && isFavoriteMessagesLoaded ? (
            <div>
              {!favoriteMessages || favoriteMessages.length === 0 ? (
                <Text type="b200" color="secondary">
                  No favorite messages available
                </Text>
              ) : (
                <div>
                  {expandedSession !== "#favoritequestions" && (
                    <div>
                      {favoriteMessages.map((favorite) => (
                        <SideMenuLink
                          key={favorite.messageId}
                          href={`#fav-${favorite.messageId}`}
                          selected={hash === `#fav-${favorite.messageId}`}
                          onClick={(hash) => {
                            setHash(hash);
                            setOpenLinkHash(
                              openLinkHash === favorite.messageId
                                ? null
                                : favorite.messageId,
                            );
                            clickFavoriteQuestion(favorite.userText);
                          }}
                          onEscape={() => {
                            setHash("#favoritequestions");
                            setOpenLinkHash(null);
                          }}
                        >
                          <div className="favorite-question-container">
                            <div className="favorite-question-text">
                              <Text
                                type="b200"
                                id="side-menu-favorite-questions-truncate"
                              >
                                {favorite.userText}
                              </Text>
                            </div>
                            <Button
                              type="icon"
                              size="small"
                              id="delete-favorite-button"
                              onClick={() => {
                                handleDeleteConfirmation(favorite.messageId);
                              }}
                            >
                              <Icon tokens={deleteTokens} />
                            </Button>
                          </div>
                          <Text
                            type="b100"
                            id="side-menu-favorite-chats-timestamp"
                          >
                            {formatTimestamp(+favorite.favoritedAt)}
                          </Text>
                        </SideMenuLink>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <Text type="b100">Loading favorite messages...</Text>
          )}
        </SideMenuLink>

        <SideMenuLink
          href="#recentChats"
          selected={hash === "#recentChats"}
          onClick={(hash) => {
            navigate(homeRoute);
            setHash(hash);
            setOpenLinkHash(openLinkHash === "#recentChats" ? null : hash);
            setExpandedSession(
              expandedSession === "#recentChats" ? null : "#recentChats",
            );
          }}
          disabled={recentChats.length === 0}
        >
          Recent chats
          {isAuthorized && isRecentChatsLoaded ? (
            <div>
              {recentChats.length === 0 ? (
                <p>No recent chats available</p>
              ) : (
                <div>
                  {expandedSession !== "#recentChats" && (
                    <div>
                      {recentChats.map((chat) => (
                        <SideMenuLink
                          key={chat.messageId}
                          href={`#${chat.messageId}`}
                          selected={hash === `#${chat.messageId}`}
                          onClick={(hash) => {
                            setHash(hash);
                            setOpenLinkHash(
                              openLinkHash === chat.messageId
                                ? null
                                : chat.messageId,
                            );
                            clickHistoryQuestion(chat.userText);
                          }}
                          onEscape={() => {
                            setHash("#recentChats");
                            setOpenLinkHash(null);
                          }}
                        >
                          <Text
                            type="b200"
                            id="side-menu-recent-chats-truncate"
                          >
                            {chat.userText}
                          </Text>
                          <Text
                            type="b100"
                            id="side-menu-recent-chats-timestamp"
                          >
                            {formatTimestamp(+chat.timestamp)}
                          </Text>
                        </SideMenuLink>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <Text type="b100">Loading recent chats...</Text>
          )}
        </SideMenuLink>
      </SideMenu>
    </Row>
  );
};

export default ChatSideMenu;
